import { Form, TabsProps, Typography } from 'antd';
import { Box, Button, Stack } from "@mui/material";
import { useEffect, useState, useCallback } from "react";
import CreateTabs from "../../Components/CreateTabs";
import CreateOrgForm from '../../Components/SubPages/CreateOrgForm';
import CreateHardwareDetailForm from '../../Components/SubPages/CreateHardwareDetailForm';
import { CREATE_ORGANIZATION, UPDATE_ORGANIZATION } from '../../Graphql/Mutations';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Error as ErrorType } from '../../Types/MessageType';
import globals from '../../Constants/Globals';
import LoadingButton from '@mui/lab/LoadingButton';
import Message from '../../Components/Message';
import { GET_ORG_BY_ID } from '../../Graphql/Queries';
import { useLocation, useNavigate } from 'react-router-dom';
import { SuperAdminPaths } from '../../Routes/Paths';
import { uploadImage } from '../../Utils/imageUpload'; // Assume this function handles the image upload
import CreateUsers from '../../Components/SubPages/GetOrgsUsers'; // Import the new Users component
import OrgNotificationTab from '../../Components/SubPages/OrgNotificationTab'; // Import the new Notification component

const CreateOrganization = () => {
  const [tab, setTab] = useState<boolean>(true);
  const location = useLocation();
  const initialTabKey = location.state?.initialTabKey || '1'; // Default to '1' if not set
  const [selectedKey, setSelectedKey] = useState<string>(initialTabKey);
  const [form] = Form.useForm();
  const [alert, setAlert] = useState<ErrorType>({ type: 'warning', message: '' });
  const [createOrganization, { loading: loadingOrganization }] = useMutation(CREATE_ORGANIZATION);
  const [updateOrganization, { loading: loadingOrg }] = useMutation(UPDATE_ORGANIZATION);
  const navigate = useNavigate();
  const [allocatedHardwares, setAllocatedHardwares] = useState<any[]>([]); // Specify type if possible
  const [deviceImages, setDeviceImages] = useState<{ [key: string]: File }>({});
  const [invoiceFiles, setInvoiceFiles] = useState<{ [key: string]: File }>({});
  const [loading, setLoading] = useState(false);
  const [adminNotifications, setAdminNotifications] = useState<Record<string, boolean>>({});

  const [getOrgaDataById, { data }] = useLazyQuery(GET_ORG_BY_ID, {
    variables: {
      organizationByIdId: location.state?.orgId
    },
    onCompleted(data) {
      const trainingPackage = data.organizationById.trainingPackage?._id;
      const training = data.organizationById.training?._id;
      const orgData = {
        ...data.organizationById,
        trainingPackage,
        training,
        userLicenseCount: data.organizationById.userLicenseCount, // Ensure userLicenseCount is set
      };

      // Extracting allotedHardwares and setting in state
      const allotedHardwares = data.organizationById.allotedHardwares || [];
      form.setFieldsValue(orgData);
      setAllocatedHardwares(allotedHardwares);
    }
  });
  console.log("Image:", data?.organizationById?.image); 
  useEffect(() => {
    if (!!location.state?.orgId) {
      getOrgaDataById();
    }
  }, [location.state?.orgId, getOrgaDataById]);

  // Add this new effect to update allocatedHardwares when form values change
  useEffect(() => {
    const formValues = form.getFieldsValue();
    const updatedHardwares = allocatedHardwares.map((hardware, index) => ({
      ...hardware,
      serialNumber: formValues[`serialNumber-${index}`],
      deviceId: formValues[`deviceId-${index}`],
      deviceModel: formValues[`deviceModel-${index}`],
      source: formValues[`source-${index}`],
      warranty: formValues[`warranty-${index}`],
      purchaseDate: formValues[`purchaseDate-${index}`],
    }));
    setAllocatedHardwares(updatedHardwares);
  }, [form]);

  const updateAllocatedHardwares = useCallback((updatedHardwareList: any[]) => {
    setAllocatedHardwares(updatedHardwareList);
  }, []);

  const items: TabsProps['items'] = [
    {
      key: '1',
      label: <h3>Company Information</h3>,
      children: <CreateOrgForm alertMessage={alert} defaultImage={data?.organizationById?.image} />
    },
    {
      key: '2',
      label: <h3>License and Hardware Details</h3>,
      children: <CreateHardwareDetailForm allotedHardwares={allocatedHardwares} organizationDetails={form.getFieldsValue()} updateParentComponent={updateAllocatedHardwares} />
    },
    {
      key: '3',
      label: <h3>Users</h3>,
      children: <CreateUsers orgId={location.state?.orgId} />
    },
    {
      key: '4',
      label: <h3>Notification</h3>,
      children: (
        <OrgNotificationTab
          users={data?.organizationById?.users || []}
          adminNotifications={adminNotifications}
          setAdminNotifications={setAdminNotifications}
        />
      )
    },
  ];

  const getFinalUserLicenseCount = (formData: any) => {
    if (
      formData.userLicenseCount !== undefined &&
      formData.userLicenseCount !== null &&
      formData.userLicenseCount !== ""
    ) {
      return Number(formData.userLicenseCount);
    }
    return data?.organizationById?.userLicenseCount;
  };

  const createOrganizationHandler = useCallback(async (value: any) => {
    const formData = form.getFieldsValue();
    const finalUserLicenseCount = getFinalUserLicenseCount(formData);
    const payload: any = {
      _id: location.state?.orgId,
      name: formData.name,
      orgAbbreviation: formData.orgAbbreviation,
      image: formData.image,
      address: formData.address,
      city: formData.city,
      country: formData.country,
      zip: formData.zip,
      phone: formData.phone,
      adminName: formData.adminName,
      adminEmail: formData.adminEmail,
      alternateEmail: formData.alternateEmail,
      alternatePhone: formData.alternatePhone,
      trainingPackage: formData.trainingPackage,
      training: formData.training,
      userLicenseCount: finalUserLicenseCount,
      allotedHardwares: allocatedHardwares.map(({ __typename, organization, ...hardware }) => ({
        ...hardware,
        invoice: hardware.invoice || '',
        deviceImage: hardware.deviceImage || '',
      })),
    };
  
    const mutation = !!location.state?.orgId ? updateOrganization : createOrganization;
    const successMessage = !!location.state?.orgId ? "Organization updated successfully" : "Organization created successfully";
  
    mutation({
      variables: { org: payload },
      context: { hasUpload: true },
      onCompleted() {
        setAlert({ type: "success", message: successMessage });
        form.resetFields();
  
        // Navigate immediately without waiting for GET_ALL_ORGANIZATIONS
        navigate(SuperAdminPaths.ORGANIZATION_PATH);
  
        setTimeout(() => {
          setAlert({ type: 'success', message: '' });
        }, globals.timeout);
      },
      onError(error) {
        setAlert({ type: 'error', message: error.message });
        setTimeout(() => {
          setAlert({ type: 'error', message: '' });
        }, globals.timeout);
      }
    });
  }, [form, location.state?.orgId, allocatedHardwares, updateOrganization, createOrganization, navigate]);  

  const handleSubmit = async () => {
    setLoading(true);
    const formData = form.getFieldsValue();
    
    const uploadPromises = [];
    const updatedHardwares = await Promise.all(
      allocatedHardwares.map(async (hardware) => {
        const updatedHardware = { ...hardware };
        updatedHardware.warranty = updatedHardware.warranty || null;
        updatedHardware.purchaseDate = updatedHardware.purchaseDate || null;

        if (hardware.deviceImage instanceof File) {
          uploadPromises.push(
            uploadImage(hardware.deviceImage).then(response => {
              updatedHardware.deviceImage = response.imageUrl;
            })
          );
        }

        if (hardware.invoice instanceof File) {
          uploadPromises.push(
            uploadImage(hardware.invoice).then(response => {
              updatedHardware.invoice = response.imageUrl;
            })
          );
        }

        return updatedHardware;
      })
    );

    if (formData.image instanceof File) {
      uploadPromises.push(
        uploadImage(formData.image).then(response => {
          formData.image = response.imageUrl;
        })
      );
    }

    await Promise.all(uploadPromises);

    const originalUsers = data?.organizationById?.users || [];

    const usersWithNotification = originalUsers
      .filter((u: any) => u.roles?.some((r: any) => r.roleCode === 'COMPANY_ADMIN' || 'BATCH_ADMIN') && u._id)
      .map((user: any) => ({
        user_id: user._id,
        emailNotification: adminNotifications[user.email] ?? false,
      }));

    const finalUserLicenseCount = getFinalUserLicenseCount(formData);
    const payload: any = {
      _id: location.state?.orgId,
      name: formData.name,
      orgAbbreviation: formData.orgAbbreviation,
      image: formData.image,
      address: formData.address,
      city: formData.city,
      country: formData.country,
      zip: formData.zip,
      phone: formData.phone,
      adminName: formData.adminName,
      adminEmail: formData.adminEmail,
      alternateEmail: formData.alternateEmail,
      alternatePhone: formData.alternatePhone || null,
      trainingPackage: formData.trainingPackage,
      training: formData.training,
      userLicenseCount: finalUserLicenseCount, // Ensure userLicenseCount is included
      allotedHardwares: updatedHardwares.map(({ __typename, organization, ...hardware }) => hardware),
    };
    // Only add users array if updating (orgId exists)
    if (location.state?.orgId) {
      payload.users = usersWithNotification;
    }
    const mutation = !!location.state?.orgId ? updateOrganization : createOrganization;
    const successMessage = !!location.state?.orgId ? "Organization updated successfully" : "Organization created successfully";

    try {
      await mutation({ variables: { org: payload } });

      setAlert({ type: "success", message: successMessage });
      form.resetFields();

      // Navigate immediately without waiting for GET_ALL_ORGANIZATIONS
      navigate(SuperAdminPaths.ORGANIZATION_PATH);

      setTimeout(() => {
        setAlert({ type: 'success', message: '' });
      }, globals.timeout);
    } catch (error) {
      console.error('Mutation error:', error);
      if (error instanceof Error) {
        setAlert({ type: 'error', message: error.message });
      } else {
        setAlert({ type: 'error', message: 'An unknown error occurred' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (isSelected: boolean, tabKey: string) => {
    if (isSelected && selectedKey !== tabKey) {
      setSelectedKey(tabKey);
    }
  };
  
  // Wrapper function to match the existing type signature
  const handleTabChangeWrapper = (isSelected: boolean, tabKey: string) => {
    handleTabChange(isSelected, tabKey);
  };

  useEffect(() => {
    if (data?.organizationById?.users) {
      const companyAdmins = data.organizationById.users.filter(
        (u: any) => u.roles?.some((r: any) => r.roleCode === 'COMPANY_ADMIN' || 'BATCH_ADMIN')
      );
      setAdminNotifications(() => {
        const initial: Record<string, boolean> = {};
        companyAdmins.forEach((admin: any) => {
          initial[admin.email] = admin.emailNotification ?? false;
        });
        return initial;
      });
    }
  }, [data?.organizationById?.users]);

  return (
    <>
      <Form
        onFinish={handleSubmit}
        form={form}
        layout={'vertical'}
        onValuesChange={() => {
          // Force re-render to update allocatedHardwares
          setAllocatedHardwares([...allocatedHardwares]);
        }}
      >
        <Box sx={{ mb: 3 }} display={'flex'} justifyContent={'flex-end'}>
          <Box flexGrow={1} sx={{ pr: 3 }}>
            {!!alert?.message ? <Message type={alert?.type} message={alert.message} variant={"filled"} /> : null}
          </Box>
          <LoadingButton
            loading={loading}
            variant="contained"
            type="submit"
            sx={{ px: 5, py: 1, color: 'white', height: '45px' }}
          >
            {location.state?.orgId ? 'Update' : 'Save'}
          </LoadingButton>
        </Box>
        <CreateTabs 
          items={items} 
          heading={'Organization Details'} 
          headingStyle={{ color: 'black' }} 
          keySelected={selectedKey} 
          defaultKey={'1'} 
          selectedTab={(isSelected: boolean, tabKey: string) => handleTabChangeWrapper(isSelected, tabKey)}
        />
      </Form>
    </>
  );
};

export default CreateOrganization;
